// @ts-nocheck
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Card,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as DragIndicatorIcon } from '../../../assets/Icons/lucideIcons/grip-vertical.svg';
import { ReactComponent as AutoAwesomeIcon } from '../../../assets/Icons/lucideIcons/sparkles.svg';
import { ReactComponent as CheckCircleOutlineIcon } from '../../../assets/Icons/lucideIcons/circle-check.svg';
import { ReactComponent as ErrorOutlineOutlinedIcon } from '../../../assets/Icons/lucideIcons/circle-alert.svg';
import Draggable from 'react-draggable';
import { SnackbarContent, useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { AI_AUTOFILL_TOAST_STATUS } from '../../../const/CommonConst';

/**
 * Clears the active AI autofill toast tracker on window
 */
const clearActiveAIAutofillToast = () => {
  globalThis._activeAIAutofillToast = null;
};

/**
 * Updates the forwarded ref from notistack with the DOM node
 */
const updateForwardedRef = (forwardedRef, element) => {
  if (typeof forwardedRef === 'function') {
    forwardedRef(element);
    return;
  }
  if (forwardedRef) {
    const mutableRef = forwardedRef;
    mutableRef.current = element;
  }
};

/**
 * Returns presentation config (header bg, icon, subtitle) based on status
 */
const getToastPresentation = (status, errorMessage, suggestionCount) => {
  if (status === AI_AUTOFILL_TOAST_STATUS.COMPLETED) {
    return {
      headerTitle: 'AI Autofill complete',
      statusClass: 'completed',
      subtitle: `${suggestionCount} suggestion${suggestionCount === 1 ? '' : 's'} ready for review.`,
    };
  }

  if (status === AI_AUTOFILL_TOAST_STATUS.FAILED) {
    return {
      headerTitle: 'AI Autofill failed',
      statusClass: 'failed',
      subtitle: errorMessage || 'Something went wrong. Please try again.',
    };
  }

  return {
    headerTitle: 'AI Autofill in progress',
    statusClass: 'in-progress',
    subtitle: 'Analyzing your document...',
  };
};

/**
 * Inner card that renders the toast body with status-based UI
 */
function InnerCard({ data, onViewSuggestions, onRetry, onDismiss }) {
  const [status, setStatus] = useState(
    data?.status || AI_AUTOFILL_TOAST_STATUS.IN_PROGRESS
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [suggestionCount, setSuggestionCount] = useState(0);

  const toastPresentation = useMemo(() => {
    return getToastPresentation(status, errorMessage, suggestionCount);
  }, [errorMessage, status, suggestionCount]);

  // Register callbacks on window so the hook can update status in-place
  useEffect(() => {
    globalThis._aiAutofillToastCallbacks = {
      onComplete: (count) => {
        setSuggestionCount(count || 0);
        setStatus(AI_AUTOFILL_TOAST_STATUS.COMPLETED);
      },
      onFail: (message) => {
        setErrorMessage(message || 'Something went wrong. Please try again.');
        setStatus(AI_AUTOFILL_TOAST_STATUS.FAILED);
      },
    };

    return () => {
      globalThis._aiAutofillToastCallbacks = null;
    };
  }, []);

  const renderStatusIcon = useCallback(() => {
    if (status === AI_AUTOFILL_TOAST_STATUS.COMPLETED) {
      return (
        <CheckCircleOutlineIcon
          className="status-icon"
          width={20}
          height={20}
        />
      );
    }
    if (status === AI_AUTOFILL_TOAST_STATUS.FAILED) {
      return (
        <ErrorOutlineOutlinedIcon
          className="status-icon"
          width={20}
          height={20}
        />
      );
    }
    return (
      <AutoAwesomeIcon className="status-icon pulse" width={20} height={20} />
    );
  }, [status]);

  const handleViewSuggestions = useCallback(() => {
    if (onViewSuggestions) {
      onViewSuggestions();
    }
    onDismiss();
  }, [onDismiss, onViewSuggestions]);

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    }
    onDismiss();
  }, [onDismiss, onRetry]);

  const fileName = useMemo(() => {
    return data?.fileName || 'Document';
  }, [data?.fileName]);

  return (
    <Card
      className="ai-autofill-notification"
      aria-live="polite"
      aria-label={toastPresentation?.headerTitle}
    >
      {/* Header with drag handle */}
      <Box className={`title ${toastPresentation?.statusClass}`}>
        <Box className="title-content">
          <DragIndicatorIcon width={20} height={20} />
          <Typography variant="body2" className="title-text">
            {toastPresentation?.headerTitle}
          </Typography>
        </Box>
        <IconButton
          className="dismiss"
          size="small"
          onClick={onDismiss}
          aria-label="Dismiss notification"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Progress bar for in-progress state */}
      {status === AI_AUTOFILL_TOAST_STATUS.IN_PROGRESS && (
        <LinearProgress className="progress" />
      )}

      {/* Body */}
      <Box className="body">
        <Box className={`icon ${toastPresentation?.statusClass}`}>
          {renderStatusIcon()}
        </Box>

        <Box className="content">
          <Typography
            variant="body1"
            noWrap
            title={fileName}
            className="file-name"
          >
            {fileName}
          </Typography>
          <Typography variant="caption" className="subtitle">
            {toastPresentation?.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box className="actions">
        {status === AI_AUTOFILL_TOAST_STATUS.IN_PROGRESS && (
          <Button
            className="action in-progress"
            variant="outlined"
            size="small"
            aria-label="AI Autofill is processing your document"
            disabled
          >
            Processing...
          </Button>
        )}

        {status === AI_AUTOFILL_TOAST_STATUS.COMPLETED && (
          <Button
            className="action view-suggestions"
            variant="contained"
            size="small"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleViewSuggestions}
            aria-label="View AI suggestions"
          >
            View Suggestions
          </Button>
        )}

        {status === AI_AUTOFILL_TOAST_STATUS.FAILED && (
          <Button
            className="action retry"
            variant="contained"
            size="small"
            onClick={handleRetry}
            aria-label="Retry AI Autofill"
          >
            Retry
          </Button>
        )}
      </Box>
    </Card>
  );
}

InnerCard.propTypes = {
  data: PropTypes.shape({
    fileName: PropTypes.string,
    status: PropTypes.oneOf([
      AI_AUTOFILL_TOAST_STATUS.IN_PROGRESS,
      AI_AUTOFILL_TOAST_STATUS.COMPLETED,
      AI_AUTOFILL_TOAST_STATUS.FAILED,
    ]),
  }).isRequired,
  onViewSuggestions: PropTypes.func,
  onRetry: PropTypes.func,
  onDismiss: PropTypes.func.isRequired,
};

InnerCard.displayName = 'InnerCard';

/**
 * Draggable toast wrapper for AI Autofill background processing.
 * Registered in SnackbarProvider as the `aiAutofill` variant.
 */
const AIAutofillProcessingToast = forwardRef(
  function AIAutofillProcessingToast(props, ref) {
    const { id, message, onViewSuggestions, onRetry } = props;
    const { closeSnackbar } = useSnackbar();
    const nodeRef = useRef(null);

    let data = {};
    try {
      data = JSON.parse(message);
    } catch (error) {
      // Fallback for malformed JSON message — log and use defaults
      console.warn('AIAutofillProcessingToast: Failed to parse message', error);
      data = {
        fileName: 'Document',
        status: AI_AUTOFILL_TOAST_STATUS.IN_PROGRESS,
      };
    }

    useEffect(() => {
      return () => {
        // Clean up active toast tracker and callbacks on unmount
        clearActiveAIAutofillToast();
        globalThis._aiAutofillToastCallbacks = null;
      };
    }, []);

    const handleDismiss = useCallback(() => {
      clearActiveAIAutofillToast();
      closeSnackbar(id);
    }, [closeSnackbar, id]);

    const setRefs = useCallback(
      (element) => {
        nodeRef.current = element;
        updateForwardedRef(ref, element);
      },
      [ref]
    );

    return (
      <Draggable handle=".ai-autofill-notification .title" nodeRef={nodeRef}>
        <SnackbarContent ref={setRefs} className="ai-autofill-notification">
          <InnerCard
            data={data}
            onViewSuggestions={onViewSuggestions}
            onRetry={onRetry}
            onDismiss={handleDismiss}
          />
        </SnackbarContent>
      </Draggable>
    );
  }
);

AIAutofillProcessingToast.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  message: PropTypes.string.isRequired,
  onViewSuggestions: PropTypes.func,
  onRetry: PropTypes.func,
};

AIAutofillProcessingToast.displayName = 'AIAutofillProcessingToast';

export default AIAutofillProcessingToast;
